import React, { forwardRef, useMemo, useState } from "react";
import {
  EditMSFilterSwitchDropdownFieldPath,
  getMSFilterRenderedItemInitialProps,
  getMSFilterSelectedLovValue,
  MSFilterRenderedItemProps,
  MSFilterSwitchDropdownInitialProps,
  MSTableFilterItemRef,
} from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/utils";
import { SHAutocomplete, SHStack } from "@components/design-systems";
import { useTheme } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import {
  ModelScreenerFilterDropdownLov,
  ModelScreenerFilterDTO,
} from "@models/model-screener/entities/modelScreenerFilter";
import { useMount, useToggle } from "react-use";
import { MSFilterResetIcon } from "../reset";
import { cloneDeep, isEmpty, isEqual } from "lodash";
import { MSFilterItemLabel } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/label";
import { MSFilterItemSize } from "@models/model-screener/enums/filterValues";

export const MSFilterSwitchDropdown = forwardRef<
  MSTableFilterItemRef,
  MSFilterRenderedItemProps
>(({ item, itemSize, itemPaths, disabled }: MSFilterRenderedItemProps, ref) => {
  const { palette } = useTheme();
  const switchDropdownFieldPaths =
    itemPaths as EditMSFilterSwitchDropdownFieldPath;
  const isSmallSize = itemSize === MSFilterItemSize.Small;

  const { getValues, setValue } = useFormContext<ModelScreenerFilterDTO>();
  const { options: originalOptions, defaultDropdownOptions } =
    getMSFilterRenderedItemInitialProps<MSFilterSwitchDropdownInitialProps>(
      item,
    ) || {};

  // States
  const [isMounted, toggleMounted] = useToggle(false);
  const [currentDropdownValues, setCurrentDropdownValues] = useState<
    ModelScreenerFilterDropdownLov[][]
  >([]);

  useMount(() => {
    const dropdownValues: ModelScreenerFilterDropdownLov[][] = [];
    switchDropdownFieldPaths.options?.forEach((optPath) => {
      const dropdownValue = getValues(
        optPath,
      ) as ModelScreenerFilterDropdownLov[];
      dropdownValues.push(dropdownValue || []);
    });
    setCurrentDropdownValues(dropdownValues);

    toggleMounted(true);
  });

  // Functions
  const handleOnDropdownValueChange = (
    lovItem: ModelScreenerFilterDropdownLov,
    dropdownIdx: number,
  ): ModelScreenerFilterDropdownLov[] | undefined => {
    const targetDropdown = cloneDeep(currentDropdownValues) || [];
    let targetDropdownLov = targetDropdown?.[dropdownIdx] || [];
    if (isEmpty(targetDropdownLov)) return;
    targetDropdownLov = getMSFilterSelectedLovValue(targetDropdownLov, lovItem);
    targetDropdown[dropdownIdx] = targetDropdownLov;

    setCurrentDropdownValues(targetDropdown);
    return targetDropdownLov;
  };

  const handleOnReset = () => {
    setCurrentDropdownValues(defaultDropdownOptions ?? []);
    switchDropdownFieldPaths.options?.forEach((optPath, idx) => {
      setValue(optPath, defaultDropdownOptions?.[idx] || []);
    });
  };

  const shouldDisplayResetButton = useMemo(() => {
    return !isEqual(currentDropdownValues, defaultDropdownOptions);
  }, [currentDropdownValues, defaultDropdownOptions]);

  return (
    <SHStack
      ref={ref}
      width={"100%"}
      bgcolor={palette.common.white}
      borderRadius={"12px"}
      alignItems={"start"}
      p={isSmallSize ? 1 : 1.5}
      gap={1.5}
      sx={{
        filter: "drop-shadow(1px 1px 2px rgba(65, 73, 89, 0.25))",
      }}
    >
      {/* Dropdown */}
      <SHStack width={"100%"} gap={1.5}>
        {originalOptions?.map((option, optionIdx) => {
          const dropdownOptions: ModelScreenerFilterDropdownLov[] =
            defaultDropdownOptions?.[optionIdx] ?? [];
          const fieldPath = switchDropdownFieldPaths.options?.[optionIdx];
          const selectedDropdownValue = currentDropdownValues?.[
            optionIdx
          ]?.find((opt) => opt.isSelected);

          return (
            <SHStack
              key={`${optionIdx}_${isMounted}`}
              width={"100%"}
              alignItems={"start"}
            >
              <MSFilterItemLabel size={itemSize} disabled={disabled}>
                {option.label}
              </MSFilterItemLabel>

              {shouldDisplayResetButton && optionIdx === 0 && (
                <MSFilterResetIcon
                  position={"absolute"}
                  right={"5%"}
                  onClick={handleOnReset}
                  disabled={disabled}
                />
              )}

              <SHStack width={"100%"}>
                <Controller
                  name={fieldPath}
                  render={({ field: { ref, ...other } }) => {
                    return (
                      <SHAutocomplete
                        disabled={disabled}
                        onChange={(_, lovItem) => {
                          const newValue = handleOnDropdownValueChange(
                            lovItem,
                            optionIdx,
                          );
                          other.onChange(newValue);
                        }}
                        autoComplete={false}
                        freeSolo={false}
                        disableClearable
                        value={selectedDropdownValue}
                        options={dropdownOptions}
                        getOptionLabel={(option) => option.name ?? ""}
                      />
                    );
                  }}
                />
              </SHStack>
            </SHStack>
          );
        })}
      </SHStack>
      {/* Dropdown */}
    </SHStack>
  );
});
